import { Moment } from "moment-timezone";
import { forwardRef, HTMLAttributes, useCallback, useContext, useEffect, useImperativeHandle, useState } from "react";
import { defineMessages, useIntl } from "react-intl";
import { IconCalendar } from "@tabler/icons-react";
import {
  autoUpdate,
  FloatingPortal,
  offset,
  useClick,
  useDismiss,
  useFloating,
  useInteractions
} from "@floating-ui/react";
import { AnyPeriod, castPeriod, classNames, isValidPeriod, Period, useIsMounted } from "@ct-react/core";
import { BookingConfig, RangePickerController, useDefaultCalendarBlock } from "@ct-react/calendar";
import { actions, booking, form } from "../../../../i18n/sharable-defs";
import { useTools } from "../../../../hooks/tools";
import { StickableContext } from "../../../layout/wrapper";
import Button from "../../../common/button";
import SpriteSvg from "../../../../components/common/svg";
import "./period.scss";

const transDefs = defineMessages({
  clean: { id: "article.on-interest.booking.clean-period", defaultMessage: "Effacer les dates" }
});

type ResumeProps = HTMLAttributes<HTMLDivElement> & Pick<Props, "value">;

type Props = {
  config?: BookingConfig<Moment>;
  value?: AnyPeriod;
  onChange?: (value?: AnyPeriod) => void;
};

export type PeriodInputHandler = {
  open: () => void;
}

const Resume = forwardRef<HTMLDivElement, ResumeProps>(({ className, value, ...props }, forwardedRef) => {

  const intl = useIntl();

  const format = useCallback((date: keyof Period<any>): string => {
    if (!value) return "";
    const casted = castPeriod(value);
    return intl.formatDate(casted[date].toDate(), { year: "numeric", month: "2-digit", day: "2-digit" });
  }, [ value, intl ]);

  const wrapperClasses = classNames("period-input", className);

  return (
    <div ref={forwardedRef}
         className={wrapperClasses}
         {...props}>
      <div className="fi-group">
        <SpriteSvg id="calendar"/>
        <label htmlFor="check-in">{intl.formatMessage(booking.checkIn)}</label>
        <input type="text"
               id="check-in"
               value={format("start")}
               placeholder={intl.formatMessage(form.datePlaceholder)}
               readOnly={true} />
      </div>
      <div className="fi-group">
        <SpriteSvg id="calendar"/>
        <label htmlFor="check-out">{intl.formatMessage(booking.checkOut)}</label>
        <input type="text"
               id="check-out"
               value={format("end")}
               placeholder={intl.formatMessage(form.datePlaceholder)}
               readOnly={true} />
      </div>
    </div>);

});

const PeriodInput = forwardRef<PeriodInputHandler, Props>(({ config, value, onChange = () => void 0 }, forwardedRef) => {

  const isMounted = useIsMounted();
  const intl = useIntl();
  const { labelizeCalendar } = useTools();
  const { sticked } = useContext(StickableContext);

  const initedPickerRange = !!value ? castPeriod(value) : undefined;
  const initedPickerFocus = !!value ? undefined : "start";

  const [ pickerOpen, setPickerOpen ] = useState<boolean>(false);
  const {
    config: [ bookingConfig, setBookingConfig ],
    range: [ pickerRange, setPickerRange ],
    focus: [ pickerFocus, setPickerFocus ],
    isDayBlocked
  } = useDefaultCalendarBlock(initedPickerRange, initedPickerFocus);

  useImperativeHandle(forwardedRef, () => ({
    open: () => setPickerOpen(true)
  }));

  useEffect(() => setBookingConfig(config), [ config ]);

  useEffect(() => {
    if (!isMounted || pickerFocus === "end" || !isValidPeriod(pickerRange)) return;
    onChange(pickerRange);
  }, [ pickerFocus ]);

  const { refs, context, floatingStyles } = useFloating({
    open: pickerOpen,
    onOpenChange: setPickerOpen,
    strategy: "absolute",
    placement: "bottom-end",
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(({ rects, elements }) => {
        elements.floating.style.setProperty("--ccv-interest-display-width", `${rects.reference.width}px`);
        return -1 * rects.reference.height;
      })
    ]
  });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    useClick(context, { toggle: false }),
    useDismiss(context, { outsidePress: true })
  ]);

  const onReset = () => {
    setPickerFocus("start");
    setPickerRange({});
    onChange(undefined);
  }

  if (sticked)
    return (
      <>
        <RangePickerController numberOfMonths={1}
                               minDate={bookingConfig?.period.start}
                               maxDate={bookingConfig?.period.end}
                               labelize={labelizeCalendar}
                               firstDayOfWeek={1}
                               selectedRange={pickerRange}
                               pickFocus={pickerFocus}
                               onSelectedRangeChange={setPickerRange}
                               onPickFocusChange={setPickerFocus}
                               isDayBlocked={isDayBlocked} />
        <Button className="reset primary"
                onClick={onReset}
                disabled={pickerFocus === "start"}>
          {intl.formatMessage(transDefs.clean)}
        </Button>
      </>);

  return (
    <>
      <Resume ref={refs.setReference}
              {...getReferenceProps()}
              value={value} />
      {pickerOpen &&
        <FloatingPortal>
          <div ref={refs.setFloating}
               {...getFloatingProps()}
               style={floatingStyles}
               className="vars-for-portal picker-wrap period">
            <div className="picker-header">
              <Resume value={value} />
            </div>
            <RangePickerController numberOfMonths={2}
                                   minDate={bookingConfig?.period.start}
                                   maxDate={bookingConfig?.period.end}
                                   labelize={labelizeCalendar}
                                   firstDayOfWeek={1}
                                   selectedRange={pickerRange}
                                   pickFocus={pickerFocus}
                                   onSelectedRangeChange={setPickerRange}
                                   onPickFocusChange={setPickerFocus}
                                   isDayBlocked={isDayBlocked} />
            <div className="picker-footer">
              <Button className="link primary"
                      onClick={onReset}
                      disabled={pickerFocus === "start"}>
                {intl.formatMessage(transDefs.clean)}
              </Button>
              <Button className="primary"
                      onClick={() => setPickerOpen(false)}>
                {intl.formatMessage(!!isValidPeriod(pickerRange) ? actions.validate : actions.close)}
              </Button>
            </div>
          </div>
        </FloatingPortal>
      }
    </>);

});

export default PeriodInput;
